import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase/config";
import { cld } from "../services/cloudinaryService";
import PhotoUploader from "./PhotoUploader";
import { aviso } from "./Avisos";
import "../styles/albumManager.css";

export default function AlbumManager({ deporte, deporteId }) {
  const [albumes, setAlbumes] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAlbum, setNewAlbum] = useState({ nombre: "", descripcion: "" });
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [editAlbum, setEditAlbum] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarAlbumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deporteId]);

  const cargarAlbumes = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "albumes"), where("deporte", "==", deporteId));
      const qs = await getDocs(q);
      const data = [];
      qs.forEach((d) => {
        const v = d.data();
        data.push({
          id: d.id,
          orden: v.orden !== undefined ? v.orden : 999,
          nombre: v.nombre,
          descripcion: v.descripcion,
          deporte: v.deporte,
          fechaCreacion: v.fechaCreacion,
          fotos: v.fotos || []
        });
      });
      data.sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));
      setAlbumes(data);
    } catch (e) {
      console.error(e);
      aviso("Error al cargar álbumes", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async () => {
    if (!newAlbum.nombre.trim()) {
      aviso("Ingresa un nombre", "error");
      return;
    }
    try {
      await addDoc(collection(db, "albumes"), {
        nombre: newAlbum.nombre,
        descripcion: newAlbum.descripcion,
        deporte: deporteId,
        fechaCreacion: new Date().toISOString(),
        orden: albumes.length,
        fotos: []
      });
      setNewAlbum({ nombre: "", descripcion: "" });
      setShowCreateForm(false);
      await cargarAlbumes();
      aviso("Álbum creado");
    } catch (e) {
      console.error(e);
      aviso("Error creando álbum", "error");
    }
  };

  const handleDeleteAlbum = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar "${nombre}"?`)) return;
    try {
      await deleteDoc(doc(db, "albumes", id));
      if (selectedAlbum?.id === id) setSelectedAlbum(null);
      await cargarAlbumes();
      aviso("Álbum eliminado");
    } catch (e) {
      console.error(e);
      aviso("Error eliminando álbum", "error");
    }
  };

  // Se lee el álbum recién de la base: durante una subida larga pudo cambiar el orden
  const guardarSubidas = async (albumId, subidas) => {
    const ref = doc(db, "albumes", albumId);
    const actuales = (await getDoc(ref)).data()?.fotos || [];
    const nuevas = subidas.map(r => ({ id: r.publicId, url: r.url, nombre: r.nombre, fechaSubida: new Date().toISOString() }));
    const fotos = [...nuevas, ...actuales];
    await updateDoc(ref, { fotos });
    setAlbumes(prev => prev.map(a => a.id === albumId ? { ...a, fotos } : a));
    setSelectedAlbum(prev => prev?.id === albumId ? { ...prev, fotos } : prev);
  };

  // Guarda el arreglo de fotos y sincroniza lista + detalle (antes la lista quedaba desactualizada)
  const guardarFotos = async (fotos) => {
    await updateDoc(doc(db, "albumes", selectedAlbum.id), { fotos });
    setAlbumes(prev => prev.map(a => a.id === selectedAlbum.id ? { ...a, fotos } : a));
    setSelectedAlbum({ ...selectedAlbum, fotos });
  };

  const handleDeletePhoto = async (photoId, photoNombre) => {
    if (!window.confirm(`¿Eliminar "${photoNombre}"?`)) return;
    try {
      await guardarFotos((selectedAlbum.fotos || []).filter(f => f.id !== photoId));
    } catch (e) {
      console.error(e);
      aviso("Error eliminando foto", "error");
    }
  };

  const moveAlbumUp = async (albumId) => {
    const i = albumes.findIndex(a => a.id === albumId);
    if (i <= 0) return;
    const arr = [...albumes];
    const [m] = arr.splice(i, 1);
    arr.splice(i - 1, 0, m);
    setAlbumes(arr);
    await Promise.all(arr.map((a, idx) => updateDoc(doc(db, "albumes", a.id), { orden: idx })));
  };

  const moveAlbumDown = async (albumId) => {
    const i = albumes.findIndex(a => a.id === albumId);
    if (i >= albumes.length - 1) return;
    const arr = [...albumes];
    const [m] = arr.splice(i, 1);
    arr.splice(i + 1, 0, m);
    setAlbumes(arr);
    await Promise.all(arr.map((a, idx) => updateDoc(doc(db, "albumes", a.id), { orden: idx })));
  };

  const movePhotoUp = async (photoId) => {
    if (!selectedAlbum) return;
    const fotos = [...selectedAlbum.fotos];
    const i = fotos.findIndex(f => f.id === photoId);
    if (i <= 0) return;
    [fotos[i - 1], fotos[i]] = [fotos[i], fotos[i - 1]];
    await guardarFotos(fotos);
  };

  const movePhotoDown = async (photoId) => {
    if (!selectedAlbum) return;
    const fotos = [...selectedAlbum.fotos];
    const i = fotos.findIndex(f => f.id === photoId);
    if (i >= fotos.length - 1) return;
    [fotos[i + 1], fotos[i]] = [fotos[i], fotos[i + 1]];
    await guardarFotos(fotos);
  };

  const setAsPortada = async (photoId) => {
    if (!selectedAlbum) return;
    const fotos = [...selectedAlbum.fotos];
    const i = fotos.findIndex(f => f.id === photoId);
    if (i === 0) return;
    const [f] = fotos.splice(i, 1);
    fotos.unshift(f);
    await guardarFotos(fotos);
  };

  const handleUpdateAlbum = async () => {
    if (!editAlbum?.nombre?.trim()) {
      aviso("Nombre obligatorio", "error");
      return;
    }
    try {
      setSaving(true);
      await updateDoc(doc(db, "albumes", selectedAlbum.id), {
        nombre: editAlbum.nombre,
        descripcion: editAlbum.descripcion,
        fechaCreacion: editAlbum.fechaCreacion,
        fechaModificacion: new Date().toISOString()
      });
      setAlbumes(albumes.map(a => a.id === selectedAlbum.id ? { ...a, ...editAlbum } : a));
      setSelectedAlbum({ ...selectedAlbum, ...editAlbum });
      aviso("Álbum actualizado");
    } catch (e) {
      console.error(e);
      aviso("Error guardando cambios", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando álbumes...</p>
      </div>
    );
  }

  return (
    <div className="album-manager">
      <div className="manager-header">
        <h2>Gestión de Álbumes</h2>
        <p>Deporte: <span className="deporte-name">{deporte.nombre}</span></p>
      </div>

      <div className="manager-content">
        <div className="albumes-panel">
          <button className="btn-create-album" onClick={() => setShowCreateForm(!showCreateForm)}>+ Nuevo Álbum</button>

          {showCreateForm && (
            <div className="create-album-form">
              <input placeholder="Nombre del álbum *" value={newAlbum.nombre} onChange={e => setNewAlbum({ ...newAlbum, nombre: e.target.value })} />
              <textarea placeholder="Descripción" rows="3" value={newAlbum.descripcion} onChange={e => setNewAlbum({ ...newAlbum, descripcion: e.target.value })} />
              <div className="form-actions">
                <button className="btn-save" onClick={handleCreateAlbum}>Crear</button>
                <button className="btn-cancel" onClick={() => setShowCreateForm(false)}>Cancelar</button>
              </div>
            </div>
          )}

          <div className="albumes-list">
            {albumes.map((album, index) => (
              <div key={album.id} className={`album-item ${selectedAlbum?.id === album.id ? "selected" : ""}`} onClick={() => {
                setSelectedAlbum(album);
                setEditAlbum({
                  nombre: album.nombre,
                  descripcion: album.descripcion || "",
                  fechaCreacion: album.fechaCreacion ? album.fechaCreacion.slice(0, 10) : ""
                });
              }}>
                <div className="album-item-header">
                  <div className="album-actions">
                    <button disabled={index === 0} onClick={(e) => { e.stopPropagation(); moveAlbumUp(album.id); }}>↑</button>
                    <button disabled={index === albumes.length - 1} onClick={(e) => { e.stopPropagation(); moveAlbumDown(album.id); }}>↓</button>
                  </div>
                  <h4>{album.nombre}</h4>
                  <button className="btn-delete-album" onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album.id, album.nombre); }}>×</button>
                </div>
                <p className="album-item-desc">{album.descripcion || "Sin descripción"}</p>
                <div className="album-item-footer"><span className="album-item-count">{album.fotos?.length || 0} fotos</span></div>
              </div>
            ))}
          </div>
        </div>

        {selectedAlbum ? (
          <div className="album-details-panel">
            <div className="album-details-header">
              <input className="album-edit-title" value={editAlbum.nombre} onChange={e => setEditAlbum({ ...editAlbum, nombre: e.target.value })} />
              <textarea className="album-edit-desc" rows="3" value={editAlbum.descripcion} onChange={e => setEditAlbum({ ...editAlbum, descripcion: e.target.value })} />
              <input type="date" className="album-edit-date" value={editAlbum.fechaCreacion} onChange={e => setEditAlbum({ ...editAlbum, fechaCreacion: e.target.value })} />
              <button className="btn-save-album" disabled={saving} onClick={handleUpdateAlbum}>Guardar cambios</button>
            </div>

            <PhotoUploader
              key={selectedAlbum.id}
              folder={`sportfolio/${deporteId}/${selectedAlbum.id}`}
              onSubidas={(subidas) => guardarSubidas(selectedAlbum.id, subidas)}
            />

            <div className="photos-grid">
              {selectedAlbum.fotos?.map((foto, index) => (
                <div key={foto.id} className="photo-manager-item">
                  <img src={cld(foto.url, 300)} alt={foto.nombre} loading="lazy" />
                  <div className="photo-actions">
                    <button className="btn-set-portada" title="Poner de portada" aria-label="Poner de portada" disabled={index === 0} onClick={() => setAsPortada(foto.id)}>★</button>
                    <button className="btn-move-photo-up" title="Mover antes" aria-label="Mover antes" disabled={index === 0} onClick={() => movePhotoUp(foto.id)}>↑</button>
                    <button className="btn-move-photo-down" title="Mover después" aria-label="Mover después" disabled={index === selectedAlbum.fotos.length - 1} onClick={() => movePhotoDown(foto.id)}>↓</button>
                    <button className="btn-delete-photo" title="Eliminar foto" aria-label="Eliminar foto" onClick={() => handleDeletePhoto(foto.id, foto.nombre)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="no-album-selected">Selecciona un álbum</div>
        )}
      </div>
    </div>
  );
}